// @ts-strict

import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NotificationTypes } from '@app/core/services/global-notifications/global-notifications.model';
import { GlobalNotificationsService } from '@app/core/services/global-notifications/global-notifications.service';
import { IvrConfigurationsService } from '@app/core/services/ivr-configurations/ivr-configurations.service';
import { IEditIVRData, IIVRConfigurations, IIVRFormData, IVRConfigurationData } from '@app/core/services/ivr-configurations/ivr-configurations.service.types';
import { isEmpty } from 'lodash';
import { NotificationType } from '@app/core/services/global-notifications/global-notifications.model';

interface FieldValue {
  values: string[];
  text: string;
  parent: string;
}

@Component({
  selector: 'app-assign-ivr-number',
  templateUrl: './assign-ivr-number.component.html',
  styleUrls: ['./assign-ivr-number.component.scss']
})
export class AssignIvrNumberComponent implements OnInit {
  @Input() configurationData?: IVRConfigurationData;

  public form: UntypedFormGroup;
  public rate1 = '145';
  public rate2 = '245';
  public currency = 'PLN';
  public currentActiveSwitch = 'sociable';
  public currentActiveRate = '';
  public isFocused = false;

  public submitted = false;
  public fieldsWithValues: Record<string, FieldValue> = {};
  private availableFields?: IEditIVRData | IIVRConfigurations;
  Object = Object;
  public isLoaded = false;
  public showEditButton = false;

  constructor(
    public activeModal: NgbActiveModal,
    private globalNotificationsService: GlobalNotificationsService,
    private ivrService: IvrConfigurationsService,
    private fb: UntypedFormBuilder
  ) {
    this.form = this.fb.group({
      name: ['', Validators.required]
    });
  }

  get isEroticChannel(): boolean {
    return this.currentActiveSwitch === 'sociable';
  }

  get isEsotericChannel(): boolean {
    return this.currentActiveSwitch === 'ezoteric';
  }

  get isEditModal(): boolean {
    return !!this.configurationData && !!Object.keys(this.configurationData).length;
  }

  get formControls() {
    return this.form.controls;
  }

  ngOnInit(): void {
    if (this.isEditModal) {
      this.getEditConfigFields();
    } else {
      this.getAddConfigFields();
    }
  }

  getEditConfigFields(): void {
    if (this.configurationData?.id) {
      this.ivrService.getFieldsForIVRNumberConfigEdit(this.configurationData.id).subscribe(res => {
        this.isLoaded = true;
        this.availableFields = res.data?.ivr_configuration;

        if (this.availableFields) {
          this.formControls.name.patchValue(this.availableFields.name);
        }
      });
    }
  }

  getAddConfigFields(): void {
    this.ivrService.getFieldsToAssignNumber().subscribe(res => {
      this.isLoaded = true;
      this.availableFields = res.data?.ivr_configurations;
      if (this.availableFields) {
        (Object.entries(this.availableFields as IIVRConfigurations) as [string, IIVRFormData][]).forEach(([key, value]) => {
          const amountValue = value.fields?.find(element => element.system_name === 'amount')?.values;

          this.fieldsWithValues[key] = {
            values: key === 'sociable' ? amountValue?.split(';') || [] : ['245'],
            text: key === 'sociable' ? 'ivr_social' : 'ivr_esoteric_chanel',
            parent: key
          };
        });
      }

      if (!isEmpty(this.fieldsWithValues)) {
        const firstValue = Object.entries(this.fieldsWithValues)[0];
        this.currentActiveSwitch = firstValue[0];
        this.currentActiveRate = firstValue[1].values[0];
      }
    });
  }

  divideNumber(num: string): number {
    return parseInt(num, 10) / 100;
  }

  changeActiveChannel(channel: string): void {
    this.currentActiveSwitch = channel;
    this.currentActiveRate = this.fieldsWithValues[channel].values[0];
  }

  getValue(): void {
    this.submitted = true;

    if (this.isEditModal) {
      if (this.formControls.name.value !== this.configurationData?.name) {
        this.showEditButton = true;
      }
    }
  }

  verify(): void {
    this.submitted = true;

    if (!this.formControls.name || this.formControls.name.invalid) {
      this.showNotification('view_translations.name_for_ivr_config_is_invalid', NotificationTypes[1]);
      return;
    } else if (this.isEditModal) {
      this.editConfiguration();
    } else {
      this.addConfiguraion();
    }
  }

  addConfiguraion(): void {
    this.ivrService
      .addIvrNumberConfiguration(this.formControls.name.value, this.currentActiveRate === this.rate1 ? this.rate1 : this.rate2, this.currentActiveSwitch)
      .subscribe(res => {
        if (res.status === 'success' && res.data?.ivr_configuration) {
          this.ivrService.numberAddedOrEdited.next(res.data.ivr_configuration);
          this.showNotification('view_translations.ivr_config_has_been_successfully_added', NotificationTypes[0]);
          this.activeModal.close();
        } else {
          this.showNotification('view_translations.ivr_config_has_been_unsuccessfully_added', NotificationTypes[1]);
        }
      });
  }

  editConfiguration(): void {
    if (this.configurationData?.id) {
      this.ivrService.updateIvrNumberConfiguration(this.configurationData.id, this.formControls.name.value).subscribe(res => {
        if (res.status === 'success' && res.data?.ivr_configuration) {
          this.ivrService.numberAddedOrEdited.next(res.data.ivr_configuration);
          this.showNotification('view_translations.ivr_config_has_been_successfully_updated', NotificationTypes[0]);
          this.activeModal.close();
        } else {
          this.showNotification('view_translations.ivr_config_has_been_unsuccessfully_updated', NotificationTypes[1]);
        }
      });
    }
  }

  isInputBlur(): void {
    const inputLength = this.formControls.name.value?.length;
    this.isFocused = false;
    this.submitted = inputLength > 0;
  }

  showNotification(message: string, type: NotificationType): void {
    this.globalNotificationsService.show({
      message,
      type,
      translate: true,
      isPersistent: false,
      id: ''
    });
  }
}
